import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Check, X } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import {
  acceptSeat,
  declineSeat,
  SeatApiError,
} from "../../features/sessions/slice-extensions/seats-slice-extension";
import { SeatRequest } from "../../features/sessions/models/SeatRequest";

interface SeatDecisionButtonsProps {
  seat: SeatRequest;
}

/** Confirm / Decline for one Requested seat — shared by the home requests card and the session page. */
const SeatDecisionButtons = ({ seat }: SeatDecisionButtonsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);

  const decide = async (accept: boolean) => {
    setBusy(true);
    const args = { bookingId: seat.bookingId, seatId: seat.seatId };
    const result = accept ? await dispatch(acceptSeat(args)) : await dispatch(declineSeat(args));
    setBusy(false);
    if (result.meta.requestStatus === "fulfilled") {
      toast.success(accept ? t("home.seatRequests.confirmed") : t("home.seatRequests.declined"));
    } else if ((result.payload as SeatApiError | undefined)?.code === "status") {
      // Someone (or another tab) already confirmed it.
      toast.info(t("home.seatRequests.alreadyHandled"));
    } else {
      toast.error(t("home.seatRequests.actionFailed"));
    }
  };

  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-white dark:text-primary-90 dark:hover:bg-primary-10"
        disabled={busy}
        onClick={() => decide(true)}
      >
        <Check size={14} />
        {t("seats.accept")}
      </button>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
        disabled={busy}
        onClick={() => decide(false)}
      >
        <X size={14} />
        {t("seats.decline")}
      </button>
    </div>
  );
};

export default SeatDecisionButtons;
